<template>
  <div>
    <b-row>
      <b-col class="d-flex justify-content-between pb-1 align-items-center">
        <h1 class="font-weight-bolder">
          {{ $t('approvedQuotes') }}
        </h1>
      </b-col>
    </b-row>

    <div class="d-flex flex-wrap">
      <b-form-group
        :label="$t('paymentStatus')"
        class="mr-1"
      >
        <b-form-select
          v-model="paymentStatus"
          :options="statusOptions"
          @change="onPaymentStatusChange"
        />

      </b-form-group>
    </div>

    <b-row>
      <b-col>
        <b-overlay :show="loading && quotes.length > 0">
          <quotes-table
            :quotes="quotes"
            :loading="loading"
            empty-title="Todavía no hay compras en esta obra"
            empty-hint="Aquí van a aparecer las cotizaciones que pagues o que te aprueben a crédito."
            :no-tools="false"
            :show-status-column="false"
            :show-actions-column="true"
            :show-invoice-column="true"
            :show-published-at-column="false"
            :show-purchased-at-column="true"
            :show-days-overdue-column="true"
            :show-delivery-status-column="true"
            @sort-changed="handleSort"
            @quote-deleted="refreshQuotes"
            @quote-nickname-updated="refreshQuotes"
            @quote-requoted="onRequoteQuote"
          />
        </b-overlay>
      </b-col>
    </b-row>

    <b-row>
      <b-col class="d-flex justify-content-end">
        <b-pagination
          :value="quotesPagination.page"
          :total-rows="quotesPagination.total_objects"
          :per-page="quotesPagination.per_page"
          aria-controls="quotes-table"
          @change="nextPage($event)"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import QuotesTable from '@/components/pricing/QuotesTable.vue'

export default {
  components: {
    QuotesTable,
  },

  data() {
    return {
      loading: false,
      sortField: null,
      sortDirection: null,

      paymentStatus: 'by_payment_status_default',
      statusOptions: [
        { text: this.$t('all'), value: 'by_payment_status_default' },
        { text: this.$t('awaiting_payment_approval'), value: 'awaiting_payment_approval' },
        { text: this.$t('payment_approved'), value: 'payment_approved' },
        { text: this.$t('payment_rejected'), value: 'payment_rejected' },
        { text: this.$t('credit_approved'), value: 'credit_approved' },
      ],
    }
  },

  computed: {
    ...mapGetters('quotes', ['quotes', 'quotesPagination']),
  },

  created() {
    this.fetchData()
  },

  destroyed() {
    this.clearQuotes()
  },

  methods: {
    ...mapMutations('quotes', {
      clearQuotes: 'CLEAR_QUOTES',
      changePagination: 'CHANGE_PAGINATION',
    }),
    ...mapActions('quotes', ['fetchQuotes', 'requoteQuote']),

    buildBaseParams() {
      const params = {
        by_project: this.$route.params.projectId,
        meta: { pagination: this.quotesPagination },
      }

      if (this.paymentStatus === 'by_payment_status_default') {
        params.by_payment_status = '1,2,4'
      } else {
        params.by_payment_status = this.paymentStatus
      }

      // sort
      if (this.sortField && this.sortDirection) {
        params.sort = `${this.sortField}:${this.sortDirection}`
      }

      return params
    },

    handleFetchQuotes() {
      this.loading = true
      return this.fetchQuotes(this.buildBaseParams())
        .finally(() => { this.loading = false })
    },

    fetchData() {
      return this.handleFetchQuotes()
    },

    refreshQuotes() {
      return this.fetchQuotes(this.buildBaseParams())
    },

    nextPage(value) {
      this.changePagination({ field: 'page', value })
      this.handleFetchQuotes()
    },

    handleSort({ field, direction }) {
      this.sortField = field
      this.sortDirection = direction
      this.changePagination({ field: 'page', value: 1 })
      this.handleFetchQuotes()
    },

    async onRequoteQuote(sourceQuoteId) {
      try {
        this.loading = true

        const newQuote = await this.requoteQuote(sourceQuoteId)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('quoteRequoted'),
            icon: 'CopyIcon',
            variant: 'success',
          },
        })

        if (String(newQuote.id) === String(sourceQuoteId)) {
          await this.refreshQuotes()
          return
        }

        this.$router.push({
          name: 'addProductsToQuote',
          params: { quoteId: newQuote.id },
        })
      } catch (err) {
        const msg = err?.data?.messages?.[0] || err?.statusText || this.$t('quoteRequoteFailed')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: msg,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },

    onPaymentStatusChange() {
      this.changePagination({ field: 'page', value: 1 })
      this.handleFetchQuotes()
    },

  },
}
</script>
